import { ErrorMessage } from "@hookform/error-message";
import { ActionIcon, Button, Flex, Input, Modal } from "@mantine/core";
import {
  Dropzone,
  type FileWithPath,
  IMAGE_MIME_TYPE,
} from "@mantine/dropzone";
import { useDisclosure } from "@mantine/hooks";
import Image from "next/image";
import { useState } from "react";
import {
  type Control,
  type FieldErrors,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";

import {
  CloseIcon,
  Group,
  PaperClipIcon,
  Stack,
  Text,
} from "@/components/ui/elements";
import { TextAreaField } from "@/components/ui/parts";

type Props<FORM_TYPE extends FieldValues> = {
  control: Control<FORM_TYPE>;
  errors?: FieldErrors;
};

export function MessageInputs<FORM_TYPE extends FieldValues>(
  props: Props<FORM_TYPE>,
) {
  const bodyName = "body" as Path<FORM_TYPE>;
  const imageName = "image" as Path<FORM_TYPE>;
  const attachmentName = "attachment" as Path<FORM_TYPE>;
  const originalFileNameName = "originalFileName" as Path<FORM_TYPE>;
  const { control, errors } = props;
  const [fileOpened, { open: openFile, close: closeFile }] = useDisclosure();
  const [files, setFiles] = useState<FileWithPath[]>([]);
  const { field: imageField } = useController({
    name: imageName,
    control: control,
  });
  const { field: attachmentField } = useController({
    name: attachmentName,
    control: control,
  });
  const { field: originalFileNameField } = useController({
    name: originalFileNameName,
    control: control,
  });
  const handleDrop = (droppedFiles: FileWithPath[]) => {
    if (droppedFiles.length === 0) return;
    const file = droppedFiles[0];
    setFiles(droppedFiles);

    if (file.type === "application/pdf") {
      attachmentField.onChange(droppedFiles);
      imageField.onChange("");
      originalFileNameField.onChange(file.name);
    } else {
      imageField.onChange(droppedFiles);
      attachmentField.onChange("");
      originalFileNameField.onChange("");
    }
  };

  const hasFile =
    (imageField.value && imageField.value.length > 0) ||
    (attachmentField.value && attachmentField.value.length > 0);
  const isPdf = attachmentField.value && attachmentField.value.length > 0;

  const Preview = () => {
    if (!hasFile || files.length === 0) return null;

    if (isPdf) {
      return (
        <Group justify="center">
          <Text>選択されたファイル: {files[0].name}</Text>
        </Group>
      );
    }

    const imageUrl = URL.createObjectURL(files[0]);
    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "400px",
          height: "300px",
          margin: "0 auto",
        }}
      >
        <Image
          src={imageUrl}
          alt="preview"
          fill
          style={{ objectFit: "contain" }}
        />
      </div>
    );
  };

  return (
    <>
      <Flex>
        <Stack w={"3.4rem"}>
          <Button h={"3.4rem"} color={"black"} onClick={openFile}>
            <PaperClipIcon size={"1.4rem"} />
          </Button>
        </Stack>
        <Stack w={"calc(100% - 3.4rem)"}>
          <TextAreaField name={bodyName} control={control} />
        </Stack>
      </Flex>
      {imageField.value && imageField.value.length > 0 && (
        <Flex align="center" gap="xs">
          <span style={{ wordBreak: "break-all", fontSize: "14px" }}>
            画像: {imageField.value[0].name}
          </span>
          <ActionIcon
            size="sm"
            color="gray"
            variant="subtle"
            onClick={() => {
              imageField.onChange("");
              setFiles([]);
            }}
          >
            <CloseIcon size="1rem" />
          </ActionIcon>
          <ErrorMessage
            errors={errors}
            name={"image"}
            render={({ message }) => <Input.Error>{message}</Input.Error>}
          />
        </Flex>
      )}
      {attachmentField.value && attachmentField.value.length > 0 && (
        <Flex align="center" gap="xs">
          <span style={{ wordBreak: "break-all", fontSize: "14px" }}>
            PDF: {attachmentField.value[0].name}
          </span>
          <ActionIcon
            size="sm"
            color="gray"
            variant="subtle"
            onClick={() => {
              attachmentField.onChange("");
              originalFileNameField.onChange("");
              setFiles([]);
            }}
          >
            <CloseIcon size="1rem" />
          </ActionIcon>
          <ErrorMessage
            errors={errors}
            name={"attachment"}
            render={({ message }) => <Input.Error>{message}</Input.Error>}
          />
        </Flex>
      )}
      <ErrorMessage
        errors={errors}
        name={"oneOfTheInputsIsRequired"}
        render={({ message }) => <Input.Error>{message}</Input.Error>}
      />

      <Modal
        opened={fileOpened}
        onClose={closeFile}
        centered
        size="lg"
        title="ファイルを添付"
      >
        <Dropzone
          mih={350}
          accept={[...IMAGE_MIME_TYPE, "application/pdf"]}
          onDrop={handleDrop}
        >
          <div
            style={{
              minHeight: "300px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {!hasFile ? <Text>ファイルを選択してください</Text> : <Preview />}
          </div>
        </Dropzone>
      </Modal>
    </>
  );
}
